import { apiFetch } from './client.js';

export type AmlAlertStatus =
  | 'OPEN'
  | 'UNDER_REVIEW'
  | 'ESCALATED'
  | 'DISMISSED'
  | 'SAR_FILED';

export type AmlAlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type AmlAlertType =
  | 'SANCTIONS_HIT'
  | 'TMS_RULE_TRIGGERED'
  | 'FRAUD_HIGH_SCORE'
  | 'UNMATCHED_CLEARING';

export type AmlAlertAging = 'fresh' | 'warning' | 'overdue';

export interface AmlAlert {
  alertId: string;
  customerId: string;
  customerMaskedName: string;
  alertType: AmlAlertType;
  severity: AmlAlertSeverity;
  status: AmlAlertStatus;
  createdAt: string;
  slaDeadline: string;
  aging: AmlAlertAging;
  daysOpen: number;
  assignedTo: string | null;
  triggeredRules: string[];
}

export interface AmlAlertDetail extends AmlAlert {
  context: Record<string, unknown>;
  relatedTransactions: {
    id: string;
    type: string;
    amountCents: string;
    currency: string;
    occurredAt: string;
  }[];
  previousAlertsForCustomer: {
    alertId: string;
    alertType: AmlAlertType;
    severity: AmlAlertSeverity;
    createdAt: string;
    status: AmlAlertStatus;
  }[];
  sarCaseId: string | null;
}

export interface AmlListFilters {
  status?: AmlAlertStatus;
  severity?: AmlAlertSeverity;
  alertType?: AmlAlertType;
  limit?: number;
  cursor?: string;
}

function toQuery(filters: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const str = qs.toString();
  return str === '' ? '' : `?${str}`;
}

export const amlApi = {
  listAlerts: (filters: AmlListFilters = {}): Promise<AmlAlert[]> =>
    apiFetch<AmlAlert[]>(
      `/admin/aml/alerts${toQuery(filters as Record<string, unknown>)}`,
    ),

  getAlert: (alertId: string): Promise<AmlAlertDetail> =>
    apiFetch<AmlAlertDetail>(`/admin/aml/alerts/${alertId}`),

  assign: (alertId: string, assigneeId: string): Promise<{ success: boolean }> =>
    apiFetch<{ success: boolean }>(`/admin/aml/alerts/${alertId}/assign`, {
      method: 'POST',
      body: { assigneeId },
    }),

  dismiss: (alertId: string, reason: string): Promise<{ success: boolean }> =>
    apiFetch<{ success: boolean }>(`/admin/aml/alerts/${alertId}/dismiss`, {
      method: 'POST',
      body: { reason },
    }),

  escalate: (
    alertId: string,
    narrative: string,
  ): Promise<{ sarCaseId: string }> =>
    apiFetch<{ sarCaseId: string }>(`/admin/aml/alerts/${alertId}/escalate`, {
      method: 'POST',
      body: { narrative },
    }),

  /**
   * An officer's own suspicion, raised from wherever they formed it. Lands in
   * the same queue as a machine-generated alert and escalates the same way.
   */
  raiseSuspicion: (input: {
    customerId: string;
    withdrawalId?: string;
    severity: string;
    narrative: string;
  }): Promise<{ alertId: string; correlationId: string }> =>
    apiFetch(`/admin/aml/suspicions`, { method: 'POST', body: input }),
};
